import {
  Listing,
  MealSummary,
  MealTag,
  OrderEstimateExtraSelection,
} from '@tovala/browser-apis-combinedapi'
import {
  MenuComponentsStandardized,
  MenuComponentStandardizedAnimatedMealCarousel,
  MenuComponentStandardizedMeal,
  MenuComponentStandardizedMealWithDecoupledAddOn,
  MenuComponentStandardizedTwoMealPicker,
  MenuComponentStandardizedTwoMealPickerMeal,
} from '@tovala/browser-apis-menu-components'
import {
  AnimatedMealCarousel,
  BackgroundImageHeader,
  ExtrasCarousel,
  Meal,
  MealBackgroundStyle,
  MealImage,
  MealImageSoldOut,
  MealImageTag,
  MealKeyNutritionalInfo,
  MealsCarousel,
  MealWithDecoupledAddOn,
  QuantityStepper,
  TextImageStack,
  TwoMealPicker,
} from '@tovala/component-library'
import { events, sourceIDs } from 'analytics/events'
import { clsx } from 'clsx'
import { useVariantByScreenSize } from 'hooks/variantByScreenSize'
import { useState } from 'react'
import { track } from 'utils/analytics'
import {
  getKeyNutritionalInfoSummary,
  hasMealTag,
} from '@tovala/browser-apis-meals'
import { getMealMultipleServingPriceSummary, TAG_IDS } from 'utils/meals'
import { getSelectedMealFiltersMetadata } from 'utils/menus'
import { storageAvailable } from 'utils/storageAvailable'
import { getBestRating, RatingsByMealID } from './hooks'
import MealRatingBadge from './MealRatingBadge'
import MenuFeedback from './MenuFeedback'
import MenuGridLayout from './MenuGridLayout'
import OrderAgainCarouselFiller from './OrderAgainCarouselFiller'
import SpecialEventBanner from './SpecialEventBanner'
import { SelectionEventSourceID } from 'types/internal'

const MenuComponentsGrid = ({
  backgroundStyle: menuBackgroundStyle = 'light',
  canDecrement,
  canIncrement,
  canModifySelections,
  components,
  listingsSelections,
  mealSelectionIDs,
  mealSelectionMax,
  selectedMealSort,
  onClickDecrement,
  onClickIncrement,
  onClickListing,
  onClickMeal,
  ratingsByMealID,
  termID,
  userID,
}: {
  backgroundStyle?: MealBackgroundStyle
  canDecrement: boolean
  canIncrement: boolean
  canModifySelections: boolean
  components: MenuComponentsStandardized
  listingsSelections: OrderEstimateExtraSelection[]
  mealSelectionIDs: number[]
  mealSelectionMax?: number
  onClickDecrement(
    opts:
      | {
          meal: MealSummary
          onClickEventSourceID?: SelectionEventSourceID
        }
      | {
          coupledMealID?: number
          index?: number
          listing: Listing
          onClickEventSourceID?: 'menu_term' | 'order_summary' | 'extras_screen'
        },
  ): void
  onClickIncrement(
    opts:
      | {
          meal: MealSummary
          onClickEventSourceID?: SelectionEventSourceID
        }
      | {
          coupledMealID?: number
          index?: number
          listing: Listing
          onClickEventSourceID?: 'menu_term' | 'order_summary' | 'extras_screen'
        },
  ): void
  onClickListing(listingID: string, coupledMealID?: number): void
  onClickMeal(mealID: number, opts?: { shouldScrollToReview?: boolean }): void
  ratingsByMealID: RatingsByMealID
  selectedMealSort?: string
  termID: number
  // Only used to report which filters are in effect, so the public menu leaves
  // it unset: it has no filtering.
  userID?: number
}) => {
  const componentChunks = createComponentChunks(components)

  const [bgImageHeaderVariant, withOrderAgainFiller] = useVariantByScreenSize<
    ['bgImageHeaderMixed' | 'bgImageHeaderTop', boolean]
  >(['bgImageHeaderMixed', true], {
    md: ['bgImageHeaderTop', false],
  })

  const addOnStepperSize = useVariantByScreenSize<'medium' | 'small'>(
    'medium',
    {
      md: 'small',
    },
  )

  const [showMenuFeedback, setShowMenuFeedback] = useState(() => {
    if (storageAvailable('localStorage')) {
      let menuFeedback: number[] | string | null =
        localStorage.getItem('menuFeedback')
      menuFeedback = menuFeedback ? JSON.parse(menuFeedback) : ''

      return (
        !menuFeedback ||
        (menuFeedback &&
          typeof menuFeedback !== 'string' &&
          !menuFeedback.includes(termID))
      )
    }
  })

  function makeExtraQuantityStepper({
    colorStyle = 'white',
    coupledMealID,
    index,
    listing,
    size = 'medium',
  }: {
    colorStyle?: 'white' | 'grey'
    coupledMealID?: number
    index?: number
    listing: Listing
    size?: 'medium' | 'small'
  }) {
    const quantity =
      listingsSelections.filter(
        (listingSelection) => listingSelection.listingID === listing.id,
      )?.length ?? 0

    if (!canModifySelections || (listing.isSoldOut && quantity === 0)) {
      return { quantity: 0, stepper: null }
    }

    return {
      quantity,
      stepper: (
        <QuantityStepper
          colorStyle={colorStyle}
          disabledDecrement={!canDecrement}
          disabledIncrement={!canIncrement || listing.isSoldOut}
          labelDecrement="Remove Extra"
          labelIncrement="Add Extra"
          min={0}
          onClickDecrement={() => {
            onClickDecrement({
              coupledMealID,
              listing,
              index,
              onClickEventSourceID: 'menu_term',
            })
          }}
          onClickIncrement={() => {
            onClickIncrement({
              coupledMealID,
              listing,
              index,
              onClickEventSourceID: 'menu_term',
            })
          }}
          quantity={quantity}
          size={size}
        />
      ),
    }
  }

  function makeMealQuantityStepper({
    meal,
    onClickEventSourceID,
    zeroQuantityText,
  }: {
    meal: { id: number; mealSummary: MealSummary }
    onClickEventSourceID?: SelectionEventSourceID
    zeroQuantityText?: string
  }) {
    const quantity = mealSelectionIDs.filter((x) => x === meal.id).length

    if (
      !canModifySelections ||
      (meal.mealSummary.isSoldOut && quantity === 0)
    ) {
      return { quantity: 0, stepper: null }
    }

    return {
      quantity,
      stepper: (
        <QuantityStepper
          disabledDecrement={!canDecrement}
          disabledIncrement={!canIncrement || meal.mealSummary.isSoldOut}
          labelDecrement="Remove Meal"
          labelIncrement="Add Meal"
          min={0}
          onClickDecrement={() => {
            onClickDecrement({ meal: meal.mealSummary, onClickEventSourceID })
          }}
          onClickIncrement={() => {
            onClickIncrement({ meal: meal.mealSummary, onClickEventSourceID })
          }}
          quantity={quantity}
          size="medium"
          zeroQuantityText={zeroQuantityText}
        />
      ),
    }
  }

  function makeTwoMealPickerMeal(
    meal: MenuComponentStandardizedTwoMealPickerMeal,
  ) {
    const { id, mealSummary } = meal

    const { stepper, quantity } = makeMealQuantityStepper({
      meal: { id, mealSummary },
    })

    return {
      ...meal,
      quantity,
      stepper,
    }
  }

  /**
   * The badge for the user's own rating. It outranks every other image tag, so
   * it also suppresses the 'Order it Again' and 'Customize' tags. Sold-out meals
   * never show one. Tapping it opens the details at the review they left.
   */
  function makeRatingTag(mealSummary: MealSummary) {
    if (mealSummary.isSoldOut) {
      return undefined
    }

    const rating = ratingsByMealID.get(mealSummary.id)

    return rating ? (
      <MealRatingBadge
        onClick={() => {
          onClickMeal(mealSummary.id, { shouldScrollToReview: true })
        }}
        stars={rating.stars}
      />
    ) : undefined
  }

  function renderMeal({
    backgroundStyle = menuBackgroundStyle,
    component,
    onClickEventSourceID,
  }: {
    backgroundStyle?: MealBackgroundStyle
    component: MenuComponentStandardizedMeal
    onClickEventSourceID?: SelectionEventSourceID
  }) {
    const {
      id,
      image,
      imageTag,
      keyNutritionalInfo,
      mealSummary,
      servingPrice,
      servings,
      surcharge,
      subtitle,
      tags,
      title,
    } = component.properties

    return (
      <Meal
        backgroundStyle={backgroundStyle}
        image={
          <MealImage
            cover={
              mealSummary.isSoldOut ? (
                <MealImageSoldOut textSize="large" />
              ) : null
            }
            image={image}
            tag={
              makeRatingTag(mealSummary) ??
              (imageTag ? (
                <MealImageTag
                  icon={
                    imageTag.icon ? (
                      <img alt="" src={imageTag.icon} />
                    ) : undefined
                  }
                >
                  {imageTag.title}
                </MealImageTag>
              ) : undefined)
            }
          />
        }
        keyNutritionalInfo={
          <MealKeyNutritionalInfo
            backgroundStyle={backgroundStyle}
            keyNutritionalInfo={keyNutritionalInfo.map((info) => {
              return {
                ...info,
                isFeatured: isFeaturedKeyNutritionalInfo(
                  info.name,
                  selectedMealSort,
                ),
              }
            })}
          />
        }
        onClickMeal={() => {
          onClickMeal(id)
        }}
        pricePerServing={getMealMultipleServingPriceSummary({
          servings,
          servingPrice,
        })}
        stepper={
          makeMealQuantityStepper({
            meal: { id, mealSummary },
            zeroQuantityText: getZerQuantityStepperText(mealSummary.tags),
            onClickEventSourceID,
          }).stepper
        }
        subtitle={subtitle}
        surcharge={surcharge}
        tags={tags}
        title={title}
      />
    )
  }

  function renderMealWithDecoupledAddOn({
    backgroundColor,
    component,
    isInCarousel = false,
    onClickEventSourceID,
  }: {
    backgroundColor?: { color: string; colorDark: string }
    component: MenuComponentStandardizedMealWithDecoupledAddOn
    isInCarousel?: boolean
    onClickEventSourceID?: SelectionEventSourceID
  }) {
    const { addOn, meal } = component.properties

    const backgroundStyle = getCarouselBackgroundStyle({
      backgroundColor,
      menuBackgroundStyle,
    })

    const addOnQuantity = listingsSelections.filter(
      (selection) => selection.listingID === addOn.listing.id,
    ).length

    // When the add-on listing is sold out and the user hasn't already selected
    // it, drop the add-on and render as a plain meal. If they selected it before
    // it went sold out, we keep the card (with a sold-out overlay) so they can
    // still remove it.
    if (addOn.listing.isSoldOut && addOnQuantity === 0) {
      return renderMeal({
        backgroundStyle,
        component: { id: component.id, properties: meal, type: 'meal' },
        onClickEventSourceID,
      })
    }

    const { stepper } = makeExtraQuantityStepper({
      colorStyle: isWhiteBackground(backgroundColor?.color) ? 'grey' : 'white',
      coupledMealID: meal.id,
      listing: addOn.listing,
      size: isInCarousel ? addOnStepperSize : 'medium',
    })

    return (
      <MealWithDecoupledAddOn
        addOn={{
          id: addOn.id,
          image: { url: addOn.imageURL },
          isSoldOut: addOn.listing.isSoldOut,
          price: addOn.price,
          title: addOn.title,
        }}
        backgroundStyle={backgroundStyle}
        meal={renderMeal({
          backgroundStyle,
          component: { id: component.id, properties: meal, type: 'meal' },
          onClickEventSourceID,
        })}
        onClickAddOn={() => {
          onClickListing(addOn.id, meal.id)
        }}
        stepper={stepper}
      />
    )
  }

  function renderAnimatedMealCarousel(
    component: MenuComponentStandardizedAnimatedMealCarousel,
  ) {
    const keyNutritionalInfoSummary = getKeyNutritionalInfoSummary({
      keyNutritionalInfo: component.properties.mealOptions.flatMap(
        (mealOption) => {
          return mealOption.keyNutritionalInfo
        },
      ),
    })

    const allTwoServings = component.properties.mealOptions.every(
      (mealOption) =>
        hasMealTag(mealOption.mealSummary.tags, TAG_IDS.TWO_SERVINGS),
    )
    const allFamily = component.properties.mealOptions.every((mealOption) =>
      hasMealTag(mealOption.mealSummary.tags, TAG_IDS.FAMILY),
    )

    // The carousel renders a single tag above the option strip, so the user's
    // rating shows on the top-level card only, never on the child selections.
    // We show the best rating across the options they've actually rated.
    const bestRating = getBestRating(
      component.properties.mealOptions
        .filter((mealOption) => !mealOption.mealSummary.isSoldOut)
        .map((mealOption) => ratingsByMealID.get(mealOption.id)),
    )

    const bestRatedMealOption = bestRating
      ? component.properties.mealOptions.find(
          (mealOption) => ratingsByMealID.get(mealOption.id) === bestRating,
        )
      : undefined

    return (
      <AnimatedMealCarousel
        backgroundStyle={menuBackgroundStyle}
        buttonTitle={component.properties.buttonTitle}
        imageTag={
          bestRating && bestRatedMealOption ? (
            <MealRatingBadge
              onClick={() => {
                onClickMeal(bestRatedMealOption.id, {
                  shouldScrollToReview: true,
                })
              }}
              stars={bestRating.stars}
            />
          ) : allTwoServings ? (
            <MealImageTag>Two-Pack</MealImageTag>
          ) : allFamily ? (
            <MealImageTag>Serves 4 together</MealImageTag>
          ) : undefined
        }
        keyNutritionalInfoSummary={
          <MealKeyNutritionalInfo
            backgroundStyle={menuBackgroundStyle}
            keyNutritionalInfo={keyNutritionalInfoSummary.map((info) => {
              return {
                ...info,
                isFeatured: isFeaturedKeyNutritionalInfo(
                  info.name,
                  selectedMealSort,
                ),
              }
            })}
          />
        }
        mealOptions={component.properties.mealOptions.map((mealOption) => {
          return {
            ...mealOption,
            keyNutritionalInfo: mealOption.keyNutritionalInfo.map((info) => {
              return {
                ...info,
                isFeatured: isFeaturedKeyNutritionalInfo(
                  info.name,
                  selectedMealSort,
                ),
              }
            }),
            surcharge:
              mealOption.servings > 1
                ? getMealMultipleServingPriceSummary({
                    servings: mealOption.servings,
                    servingPrice: mealOption.servingPrice,
                  })
                : mealOption.surcharge,
            ...makeMealQuantityStepper({ meal: mealOption }),
          }
        })}
        onClickMeal={onClickMeal}
        onScrollNext={({ mealInView }) => {
          if (mealInView) {
            track(events.DID_TAP_CUSTOMIZE_IT_ARROW_RIGHT, {
              meal_id: mealInView.id,
              term_id: termID,
            })
          }
        }}
        onScrollPrev={({ mealInView }) => {
          if (mealInView) {
            track(events.DID_TAP_CUSTOMIZE_IT_ARROW_LEFT, {
              meal_id: mealInView.id,
              term_id: termID,
            })
          }
        }}
      />
    )
  }

  function renderTwoMealPicker(
    component: MenuComponentStandardizedTwoMealPicker,
  ) {
    return (
      <TwoMealPicker
        backgroundStyle={menuBackgroundStyle}
        mealOptions={[
          makeTwoMealPickerMeal(component.properties.meals[0]),
          makeTwoMealPickerMeal(component.properties.meals[1]),
        ]}
        onClickMeal={onClickMeal}
        onClickMealOption={() => {
          // Pass
        }}
      />
    )
  }

  return (
    <div className="space-y-10 md:space-y-6">
      {componentChunks.map((chunk, index) => {
        const widthClass = chunk.some(
          (component) =>
            component.type === 'mealCarousel' ||
            component.type === 'orderAgainCarousel',
        )
          ? 'max-w-[838px]'
          : 'max-w-menu'
        return (
          <div key={`chunk-${index}`} className={`mx-auto ${widthClass}`}>
            <MenuGridLayout key={`chunk-${index}`}>
              {chunk.map((component, index) => {
                if (component.type === 'meal') {
                  return (
                    <div
                      key={`meal-${component.properties.id}`}
                      className="md:px-4"
                    >
                      {renderMeal({
                        component,
                      })}
                    </div>
                  )
                } else if (component.type === 'mealWithDecoupledAddOn') {
                  return (
                    <div
                      key={`mealWithDecoupledAddOn-${component.properties.meal.id}`}
                      className="md:px-4"
                    >
                      {renderMealWithDecoupledAddOn({ component })}
                    </div>
                  )
                } else if (component.type === 'twoMealPicker') {
                  return (
                    <div
                      key={`twoMealPicker-${component.properties.meals[0].id}`}
                      className="md:px-4"
                    >
                      {renderTwoMealPicker(component)}
                    </div>
                  )
                } else if (component.type === 'animatedMealCarousel') {
                  return (
                    <div
                      key={`mealCarousel-${component.properties.mealOptions[0].id}`}
                    >
                      {renderAnimatedMealCarousel(component)}
                    </div>
                  )
                } else if (component.type === 'extrasCarousel') {
                  return (
                    <div
                      key={`extrasCarousel-${index}`}
                      className="col-span-2 md:col-span-1"
                    >
                      <ExtrasCarousel
                        extras={component.properties.children.map(
                          (child, index) => {
                            const { id, imageURL, listing, price, title } =
                              child.properties
                            const { stepper, quantity } =
                              makeExtraQuantityStepper({
                                index,
                                listing,
                              })
                            return {
                              id,
                              imageURL,
                              isSoldOut: listing.isSoldOut,
                              onClickListing: () => {
                                onClickListing(id)
                              },
                              price,
                              stepper,
                              quantity,
                              title,
                            }
                          },
                        )}
                        title={component.properties.title}
                      />
                    </div>
                  )
                } else if (component.type === 'mealCarousel') {
                  const mealCarouselBackgroundStyle =
                    getCarouselBackgroundStyle({
                      backgroundColor: component.properties.backgroundColor,
                      menuBackgroundStyle,
                    })

                  return (
                    <div
                      key={`mealCarousel-${component.id}`}
                      className="col-span-2 md:col-span-1"
                    >
                      <MealsCarousel
                        backgroundColor={component.properties.backgroundColor}
                        eyebrow={component.properties.eyebrow}
                        meals={component.properties.children.map((child) => {
                          if (child.type === 'meal') {
                            return renderMeal({
                              backgroundStyle: mealCarouselBackgroundStyle,
                              component: child,
                              onClickEventSourceID: 'meals_carousel',
                            })
                          }

                          return renderMealWithDecoupledAddOn({
                            backgroundColor:
                              component.properties.backgroundColor,
                            component: child,
                            isInCarousel: true,
                            onClickEventSourceID: 'meals_carousel',
                          })
                        })}
                        subtitle={component.properties.subtitle}
                        title={component.properties.title}
                      />
                    </div>
                  )
                } else if (
                  component.type === 'orderAgainCarousel' &&
                  component.properties.children !== undefined
                ) {
                  const orderAgainBackgroundStyle = getCarouselBackgroundStyle({
                    backgroundColor: component.properties.backgroundColor,
                    menuBackgroundStyle,
                  })

                  let orderAgainSlides = component.properties.children
                    .map((child) => {
                      if (child.type === 'meal') {
                        return renderMeal({
                          backgroundStyle: orderAgainBackgroundStyle,
                          component: child,
                          onClickEventSourceID: sourceIDs.ORDER_AGAIN_CAROUSEL,
                        })
                      } else if (child.type === 'mealWithDecoupledAddOn') {
                        return renderMealWithDecoupledAddOn({
                          backgroundColor: component.properties.backgroundColor,
                          component: child,
                          isInCarousel: true,
                          onClickEventSourceID: sourceIDs.ORDER_AGAIN_CAROUSEL,
                        })
                      }
                    })
                    .filter((child) => child !== undefined)

                  if (orderAgainSlides.length === 1 && withOrderAgainFiller) {
                    orderAgainSlides = [
                      ...orderAgainSlides,
                      <OrderAgainCarouselFiller key="orderAgainCarouselFiller" />,
                    ]
                  }

                  return (
                    <div
                      key={`orderAgainCarousel-${component.id}`}
                      className="col-span-2 md:col-span-1"
                    >
                      <MealsCarousel
                        backgroundColor={component.properties.backgroundColor}
                        eyebrow={component.properties.eyebrow}
                        meals={orderAgainSlides}
                        subtitle={component.properties.subtitle}
                        title={component.properties.title}
                      />
                    </div>
                  )
                } else if (component.type === 'textBanner') {
                  return (
                    <div
                      key={`textBanner-${index}`}
                      className="col-span-2 md:col-span-1 md:px-4"
                    >
                      <SpecialEventBanner>
                        {component.properties.title}
                      </SpecialEventBanner>
                    </div>
                  )
                } else if (
                  showMenuFeedback &&
                  component.type === 'menuFeedback'
                ) {
                  return (
                    <div
                      key={`menuFeedback-${index}`}
                      className="col-span-2 md:col-span-1 md:px-4"
                    >
                      <MenuFeedback
                        backgroundStyle={menuBackgroundStyle}
                        onMenuFeedbackSaved={(rating) => {
                          const { enabled_filters, enabled_filters_count } =
                            getSelectedMealFiltersMetadata({ userID })

                          track(events.RATES_MENU, {
                            rating,
                            term_id: termID,
                            meal_selection_count: mealSelectionIDs.length,
                            meal_selection_max: mealSelectionMax,
                            enabled_filters,
                            enabled_filters_count,
                          })

                          setShowMenuFeedback(false)
                        }}
                        options={component.properties.options}
                        subtitle={component.properties.subtitle}
                        termID={termID}
                        title={component.properties.title}
                      />
                    </div>
                  )
                } else if (component.type === 'backgroundImageHeader') {
                  return (
                    <div
                      key={`backgroundImageHeader-${index}`}
                      className={clsx(
                        'min-h-96 md:aspect-4/3 sm:aspect-square',
                        {
                          '-mt-6':
                            index === 0 &&
                            bgImageHeaderVariant === 'bgImageHeaderTop',
                        },
                      )}
                    >
                      <BackgroundImageHeader
                        image={component.properties.image}
                        subtitle={component.properties.subtitle}
                        subtitleColor={component.properties.subtitleColor}
                        title={component.properties.title}
                        titleColor={component.properties.titleColor}
                      />
                    </div>
                  )
                } else if (component.type === 'textImageStack') {
                  return (
                    <div key={index} className="col-span-2 md:col-span-1">
                      <TextImageStack
                        backgroundStyle={menuBackgroundStyle}
                        image={component.properties.image}
                        subtitle={component.properties.subtitle}
                        title={component.properties.title}
                      >
                        <MenuGridLayout>
                          {component.properties.children.map((child) => {
                            if (child.type === 'meal') {
                              return (
                                <div
                                  key={`meal-${child.properties.id}`}
                                  className="md:px-4"
                                >
                                  {renderMeal({
                                    component: child,
                                  })}
                                </div>
                              )
                            } else if (child.type === 'twoMealPicker') {
                              return (
                                <div
                                  key={`twoMealPicker-${child.properties.meals[0].id}`}
                                  className="md:px-4"
                                >
                                  {renderTwoMealPicker(child)}
                                </div>
                              )
                            } else if (child.type === 'animatedMealCarousel') {
                              return (
                                <div
                                  key={`mealCarousel-${child.properties.mealOptions[0].id}`}
                                >
                                  {renderAnimatedMealCarousel(child)}
                                </div>
                              )
                            }
                          })}
                        </MenuGridLayout>
                      </TextImageStack>
                    </div>
                  )
                }
              })}
            </MenuGridLayout>
          </div>
        )
      })}
    </div>
  )
}

export default MenuComponentsGrid

function isFeaturedKeyNutritionalInfo(
  nutritionInfoName: string,
  selectedMealSort?: string | null,
) {
  return (
    (nutritionInfoName === 'cal' && selectedMealSort === 'calories') ||
    (nutritionInfoName === 'protein' && selectedMealSort === 'protein')
  )
}

// We need to create chunks of components so we can apply a wider width to the carousel components
function createComponentChunks(components: MenuComponentsStandardized) {
  const result = []
  let currentChunk = []

  for (const item of components) {
    if (item.type === 'mealCarousel' || item.type === 'orderAgainCarousel') {
      if (currentChunk.length > 0) {
        result.push(currentChunk) // Add the last chunk if it's not empty
        currentChunk = []
      }
      result.push([item]) // Create the carousel as its own chunk
    } else {
      currentChunk.push(item)
    }
  }

  if (currentChunk.length > 0) {
    result.push(currentChunk) // Add the last chunk if it's not empty
  }
  return result
}

function isWhiteBackground(color: string | undefined) {
  if (!color) {
    return false
  }
  const normalized = color.trim().toLowerCase()
  return normalized === '#ffffff' || normalized === '#fff'
}

function getCarouselBackgroundStyle({
  backgroundColor,
  menuBackgroundStyle,
}: {
  backgroundColor: { color: string } | undefined
  menuBackgroundStyle: MealBackgroundStyle
}): MealBackgroundStyle {
  // A carousel without a background color of its own is transparent, so its
  // meals sit on whatever surface the menu section paints behind it.
  if (!backgroundColor) {
    return menuBackgroundStyle
  }

  return isWhiteBackground(backgroundColor.color) ? 'light' : 'tinted'
}

function getZerQuantityStepperText(tags: MealTag[]) {
  return hasMealTag(tags, TAG_IDS.FAMILY)
    ? 'Family Meal'
    : hasMealTag(tags, TAG_IDS.TWO_SERVINGS)
      ? 'Two-Pack'
      : ''
}
